import { useState, useEffect, useRef, useCallback } from 'react';
import { FiasWebSocket } from '../websocket';
import { authApi } from '../auth-api';

/**
 * Configuration options for the useStreaming hook
 */
export interface UseStreamingOptions {
  /** Request ID to subscribe to */
  requestId: string | null;
  /** JWT auth token for WebSocket connection */
  authToken: string | null;
  /** WebSocket endpoint URL */
  endpoint: string | null;
  /** Whether streaming is enabled (default: true) */
  enabled?: boolean;
  /** Callback when streaming text updates */
  onTextUpdate?: (nodeId: string, text: string) => void;
  /** Callback when thinking text updates (for models with extended thinking) */
  onThinkingUpdate?: (nodeId: string, text: string) => void;
  /** Callback when a node completes */
  onNodeComplete?: (nodeId: string) => void;
  /** Callback when the entire request completes (payload contains extra fields from the complete event) */
  onComplete?: (requestId: string, payload?: Record<string, unknown>) => void;
  /** Callback when an error occurs */
  onError?: (code: string, message: string) => void;
  /** Callback when UI definition is updated (for builder) */
  onUiUpdate?: (uiDefinition: unknown) => void;
  /** Callback when session settings are updated (e.g. guided phase changes) */
  onSettingsUpdate?: (settings: Record<string, unknown>) => void;
  /** Callback when files are updated (from plugin builder tools) */
  onFilesUpdate?: (files: Record<string, unknown>) => void;
  /** Callback when system prompt is received (verbose mode) */
  onSystemPrompt?: (prompt: string) => void;
  /** Callback when a tool is called (verbose mode) */
  onToolCall?: (toolName: string, toolInput: unknown) => void;
  /** Callback when a tool result is received (verbose mode) */
  onToolResult?: (toolName: string, toolResult: unknown, isError: boolean) => void;
  /** Callback when model info is received (verbose mode) */
  onModelInfo?: (model: string, intent: string) => void;
  /** Callback when tools available list is received (verbose mode) */
  onToolsAvailable?: (tools: Array<{ name: string; description: string }>) => void;
}

/**
 * Result returned by the useStreaming hook
 */
export interface UseStreamingResult {
  /** Current connection state */
  connectionState: 'disconnected' | 'connecting' | 'connected' | 'reconnecting';
  /** Whether currently connected */
  isConnected: boolean;
  /** Whether subscribed to a request */
  isSubscribed: boolean;
  /** Accumulated text for each node */
  streamingText: Record<string, string>;
  /** Accumulated thinking text for each node (for models with extended thinking) */
  streamingThinking: Record<string, string>;
  /** Set of completed node IDs */
  completedNodes: Set<string>;
  /** Whether the entire request has completed */
  isComplete: boolean;
  /** Last error (if any) */
  error: { code: string; message: string } | null;
  /** Manually connect to WebSocket */
  connect: () => Promise<void>;
  /** Manually disconnect from WebSocket */
  disconnect: () => void;
  /** Reset streaming state (clear text and completions) */
  reset: () => void;
}

/**
 * React hook for managing WebSocket streaming connections
 *
 * Automatically connects when authToken and endpoint are provided,
 * and subscribes to the requestId when it changes.
 *
 * @example
 * ```tsx
 * function StreamingComponent({ requestId }: { requestId: string }) {
 *   const {
 *     connectionState,
 *     streamingText,
 *     isComplete,
 *     error,
 *   } = useStreaming({
 *     requestId,
 *     authToken: 'your-jwt-token',
 *     endpoint: 'wss://api.example.com/stream',
 *   });
 *
 *   if (error) {
 *     return <div>Error: {error.message}</div>;
 *   }
 *
 *   return (
 *     <div>
 *       {Object.entries(streamingText).map(([nodeId, text]) => (
 *         <div key={nodeId}>
 *           <h3>{nodeId}</h3>
 *           <p>{text}</p>
 *         </div>
 *       ))}
 *       {isComplete && <div>Complete!</div>}
 *     </div>
 *   );
 * }
 * ```
 */
export function useStreaming(options: UseStreamingOptions): UseStreamingResult {
  const {
    requestId,
    authToken,
    endpoint,
    enabled = true,
    onTextUpdate,
    onThinkingUpdate,
    onNodeComplete,
    onComplete,
    onError,
    onUiUpdate,
    onSettingsUpdate,
    onFilesUpdate,
    onSystemPrompt,
    onToolCall,
    onToolResult,
    onModelInfo,
    onToolsAvailable,
  } = options;

  // State
  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'reconnecting'
  >('disconnected');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [streamingText, setStreamingText] = useState<Record<string, string>>({});
  const [streamingThinking, setStreamingThinking] = useState<Record<string, string>>({});
  const [completedNodes, setCompletedNodes] = useState<Set<string>>(new Set());
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<{ code: string; message: string } | null>(null);

  // Refs
  const wsRef = useRef<FiasWebSocket | null>(null);
  const cleanupRef = useRef<(() => void)[]>([]);
  const lastSubscribedRequestIdRef = useRef<string | null>(null);

  // Reset state
  const reset = useCallback(() => {
    console.log('[useStreaming] reset() called', new Error().stack);
    setStreamingText({});
    setStreamingThinking({});
    setCompletedNodes(new Set());
    setIsComplete(false);
    setError(null);
    setIsSubscribed(false);
    lastSubscribedRequestIdRef.current = null;
  }, []);

  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!authToken || !endpoint) {
      console.warn('Cannot connect: missing authToken or endpoint');
      return;
    }

    if (wsRef.current?.isConnected()) {
      return;
    }

    // Get a fresh token before connecting (handles token refresh if expired)
    const freshToken = await authApi.getValidIdToken();
    if (!freshToken) {
      setError({
        code: 'UNAUTHORIZED',
        message: 'Session expired. Please log out and log back in.',
      });
      return;
    }

    // Create new WebSocket instance if needed
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    try {
      await wsRef.current.connect(freshToken);
    } catch (err) {
      setError({
        code: 'CONNECTION_FAILED',
        message: err instanceof Error ? err.message : 'Failed to connect',
      });
    }
  }, [authToken, endpoint]);

  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    wsRef.current?.disconnect();
    reset();
  }, [reset]);

  // Set up WebSocket event handlers
  useEffect(() => {
    if (!enabled || !endpoint) {
      return;
    }

    // Create WebSocket instance
    if (!wsRef.current) {
      wsRef.current = new FiasWebSocket({ endpoint });
    }

    const ws = wsRef.current;

    // Set up event handlers
    const unsubConnectionChange = ws.onConnectionChange((state) => {
      setConnectionState(state);
    });

    const unsubToken = ws.onToken((nodeId, text) => {
      setStreamingText((prev) => {
        const newText = (prev[nodeId] || '') + text;
        console.log('[useStreaming] Token received', { nodeId, newLength: newText.length });
        return {
          ...prev,
          [nodeId]: newText,
        };
      });
      onTextUpdate?.(nodeId, text);
    });

    const unsubThinking = ws.onThinking((nodeId, text) => {
      setStreamingThinking((prev) => ({
        ...prev,
        [nodeId]: (prev[nodeId] || '') + text,
      }));
      onThinkingUpdate?.(nodeId, text);
    });

    const unsubNodeComplete = ws.onNodeComplete((nodeId) => {
      setCompletedNodes((prev) => new Set(prev).add(nodeId));
      onNodeComplete?.(nodeId);
    });

    const unsubComplete = ws.onComplete((completedRequestId, payload) => {
      setIsComplete(true);
      onComplete?.(completedRequestId, payload);
    });

    const unsubError = ws.onError((code, message) => {
      setError({ code, message });
      onError?.(code, message);
    });

    const unsubSubscribed = ws.onSubscribed(() => {
      setIsSubscribed(true);
    });

    const unsubUiUpdate = ws.onUiUpdate((_nodeId, uiDefinition) => {
      onUiUpdate?.(uiDefinition);
    });

    const unsubSettingsUpdate = ws.onSettingsUpdate((settings, _requestId) => {
      onSettingsUpdate?.(settings);
    });

    const unsubFilesUpdate = ws.onFilesUpdate((files, _requestId) => {
      onFilesUpdate?.(files);
    });

    const unsubSystemPrompt = ws.onSystemPrompt((prompt, _requestId) => {
      onSystemPrompt?.(prompt);
    });

    const unsubToolCall = ws.onToolCall((toolName, toolInput, _requestId) => {
      onToolCall?.(toolName, toolInput);
    });

    const unsubToolResult = ws.onToolResult((toolName, toolResult, isError, _requestId) => {
      onToolResult?.(toolName, toolResult, isError);
    });

    const unsubModelInfo = ws.onModelInfo((model, intent, _requestId) => {
      onModelInfo?.(model, intent);
    });

    const unsubToolsAvailable = ws.onToolsAvailable((tools, _requestId) => {
      onToolsAvailable?.(tools);
    });

    // Store cleanup functions
    cleanupRef.current = [
      unsubConnectionChange,
      unsubToken,
      unsubThinking,
      unsubNodeComplete,
      unsubComplete,
      unsubError,
      unsubSubscribed,
      unsubUiUpdate,
      unsubSettingsUpdate,
      unsubFilesUpdate,
      unsubSystemPrompt,
      unsubToolCall,
      unsubToolResult,
      unsubModelInfo,
      unsubToolsAvailable,
    ];

    // Cleanup on unmount
    return () => {
      cleanupRef.current.forEach((fn) => fn());
      cleanupRef.current = [];
    };
  }, [
    enabled,
    endpoint,
    onTextUpdate,
    onThinkingUpdate,
    onNodeComplete,
    onComplete,
    onError,
    onUiUpdate,
    onSettingsUpdate,
    onFilesUpdate,
    onSystemPrompt,
    onToolCall,
    onToolResult,
    onModelInfo,
    onToolsAvailable,
  ]);

  // Auto-connect when authToken, endpoint, and a requestId are available.
  // Disconnect when there's no active request to avoid idle reconnection loops.
  useEffect(() => {
    if (enabled && authToken && endpoint && requestId) {
      connect();
    } else if (!requestId && wsRef.current) {
      wsRef.current.disconnect();
    }
  }, [enabled, authToken, endpoint, requestId, connect]);

  // Subscribe to requestId when it changes
  useEffect(() => {
    if (!enabled || !requestId || connectionState !== 'connected') {
      return;
    }

    // Only reset if this is a NEW requestId, not a reconnection to the same one
    if (lastSubscribedRequestIdRef.current !== requestId) {
      console.log('[useStreaming] New requestId, resetting state', {
        oldRequestId: lastSubscribedRequestIdRef.current,
        newRequestId: requestId,
      });
      reset();
      lastSubscribedRequestIdRef.current = requestId;
    } else {
      console.log('[useStreaming] Same requestId, not resetting (reconnection)', { requestId });
    }

    // Subscribe to the new request
    wsRef.current?.subscribe(requestId);
  }, [enabled, requestId, connectionState, reset]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      wsRef.current?.disconnect();
      wsRef.current = null;
    };
  }, []);

  return {
    connectionState,
    isConnected: connectionState === 'connected',
    isSubscribed,
    streamingText,
    streamingThinking,
    completedNodes,
    isComplete,
    error,
    connect,
    disconnect,
    reset,
  };
}

export default useStreaming;
